import _ from 'lodash';
import * as members from '../privates/privateMembers';
import platformUtils from '../privates/platformUtils';
import {validate} from '../privates/validations/validation';
import reporter from '../privates/reporter';
import {capitalize} from '../privates/utils';
import BaseSite from '../namespaces/site';
import * as postMessage from '../privates/postMessage';
import siteImpl from '../namespaces/impl/siteImpl';
import {generateError} from '../privates/validations/errors';

class Site extends BaseSite {
    constructor(appDefId) {
        super(appDefId);
        _.bindAll(this, ['setPageMetaData', 'onInstanceChanged', 'isAppSectionInstalled', 'notifyEventToEditorApp']);
    }

    getSiteStructure() {
        return siteImpl.getSiteStructure({includePageId: true});
    }

    getSectionUrl(sectionIdentifier) {
        const appDefId = members._appDefId.get(this);
        const sectionId = _.get(sectionIdentifier, 'sectionId');
        const isValid = validate({
            compType: 'sectionUrl',
            functionName: 'getSectionUrl',
            dataToValidate: {sectionId}
        });
        if (!isValid) {
            return null;
        }

        const pages = platformUtils.getRMI().getSiteStructure().pages;
        const appPages = _.filter(pages, {applicationId: appDefId}, {});
        const page = _.find(appPages, {tpaPageId: sectionId}) || appPages[0];

        if (!page) {
            const errorMessage = `Page with app "${appDefId}" was not found.`;
            reporter.reportSdkError(errorMessage);
            return {error: {message: errorMessage}};
        }

        const baseUrl = platformUtils.getRGI().getNavigationInfo().baseUrl;
        const contactLink = {url: `${baseUrl}${page.url}`, relativeUrl: `${page.url}`};

        return contactLink;
    }

    getCurrentPageInfo() {
        const RMI = platformUtils.getRMI();
        const currentPageId = platformUtils.getRGI().getNavigationInfo().currentPageId;
        const pages = RMI.getSiteStructure().pages;
        const currentPage = _.find(pages, {id: currentPageId});

        const title = _.get(RMI.getPageData(), 'currentNavigationInfo.title');
        const currentPageInfo = {
            isHomePage: currentPage.isMainPage || false,
            title,
            url: currentPage.url
        };

        return currentPageInfo;
    }

    get currentPage() {
        return siteImpl.getCurrentPage({ includePageId: true });
    }

    setPageMetaData(metaData, compId) {
        const isAllowed = compId => {
            const RMI = platformUtils.getRMI();

            const componentData = RMI.getData(compId);
            if (!componentData) {
                return false;
            }
            
            const isSection = componentData.type === 'TPA';
            const isMultiSection = componentData.type === 'TPAMultiSection';

            return isSection || isMultiSection;
        };

        return new Promise((resolve, reject) => {
            if (!isAllowed(compId)) {
                return reject('Setting a page meta data is possible only from TPA Sections and MultiSections');
            }

            const {title, description, jsonLd, metaTags} = metaData;
            const appDefId = members._appDefId.get(this);
            const callback = (data, error) => { return error ? reject(error) : resolve(data); };

            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.SET_PAGE_METADATA,
                data: {
                    compId,
                    appDefId,
                    title,
                    description,
                    jsonLd,
                    metaTags
                },
                appDefinitionId: appDefId,
                callback
            });
        });
    }

    onInstanceChanged(callback) {
        const RGI = platformUtils.getRGI();
        const appDefinitionId = members._appDefId.get(this);
        RGI.addOnInstanceChangedCallback(appDefinitionId, callback);
    }

    isAppSectionInstalled(sectionIdentifier) {
        const validationObj = {sectionIdentifier, ...sectionIdentifier};
        const isValid = validate({
            compType: 'sectionIdentifier',
            functionName: 'isAppSectionInstalled',
            dataToValidate: validationObj
        });
        if (!isValid) {
            return null;
        }

        const appDefId = _.get(sectionIdentifier, 'appDefinitionId') || members._appDefId.get(this);
        const sectionId = _.get(sectionIdentifier, 'sectionId');
        const pages = platformUtils.getRMI().getSiteStructure().pages;

        const page = _.find(pages, {applicationId: appDefId, tpaPageId: sectionId});
        return !_.isUndefined(page);
    }

    getPublicAPI(appDefinitionId) {
        return new Promise((resolve, reject) => {
            const RMI = platformUtils.getRMI();
            const api = RMI.getAppPublicAPI(appDefinitionId);
            if (_.isEmpty(api)) {
                reject(`Error - getPublicAPI of ${appDefinitionId} does not exist or app did not expose a public api`);
            } else {
                resolve(api);
            }
        });
    }

    notifyEventToEditorApp(appDefinitionId, eventData) {
        if (platformUtils.getRGI().getLivePreviewMode && platformUtils.getRGI().getLivePreviewMode()){
            return;
        }
        if (capitalize(platformUtils.getQueryParameter('viewMode')) !== 'Preview'){
            reporter.reportSdkError(generateError.error_function_exist_only_in_preview({functionName: 'notifyEventToEditorApp'}));
            return;
        }
        if(validate({compType: 'Site', functionName: 'notifyEventToEditorApp', dataToValidate: {eventData}})){
            const appDefinitionIdToSend = appDefinitionId || members._appDefId.get(this);
            postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                command: postMessage.MessageTypes.NOTIFY_EVENT_TO_EDITOR_APP,
                data: {
                    eventData,
                    appDefinitionId: appDefinitionIdToSend
                },
                appDefinitionId: appDefinitionIdToSend
            });
        }
    }
}

export default Site;

